import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MathService } from '@portal-core/general/services/math.service';
import { ThemeService } from '@portal-core/general/services/theme.service';
import { ProjectChecklistItemLegend } from '@portal-core/project-checklists/enums/project-checklist-item-legend.enum';
import { ProjectChecklistAnalytics } from '@portal-core/project-checklists/models/project-checklist-analytics.model';
import { ProjectChecklist } from '@portal-core/project-checklists/models/project-checklist.model';
import { DoughnutChartHoleType } from '@portal-core/ui/charts/enums/doughnut-chart-hole-type.enum';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { ChartData, ChartOptions } from 'chart.js';
import { Observable, of, switchMap } from 'rxjs';

interface Statuses {
  Completed: number;
  ToDo: number;
  InProgress: number;
}

interface ProjectChecklistChartDataAndStatuses {
  Data: ChartData;
  Statuses: Statuses;
}

@Component({
  selector: 'mc-project-checklist-doughnut-chart',
  templateUrl: './project-checklist-doughnut-chart.component.html',
  styleUrls: ['./project-checklist-doughnut-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ProjectChecklistDoughnutChartComponent implements OnInit {
  @Input() checklist: ProjectChecklist;
  @InputObservable('checklist') checklist$: Observable<ProjectChecklist>;

  DoughnutChartHoleType: typeof DoughnutChartHoleType = DoughnutChartHoleType;

  chartOptions: ChartOptions<'doughnut'> = {
    cutout: '85%'
  };

  chartDataAndStatuses$: Observable<ProjectChecklistChartDataAndStatuses>;
  completionData: { [name: string]: { Percent: number } };

  constructor(private themeService: ThemeService, private mathService: MathService) { }

  ngOnInit() {
    this.chartDataAndStatuses$ = this.checklist$.pipe(
      switchMap(checklist => {
        if (checklist && this.checklist.Analytics && Array.isArray(this.checklist.Analytics.TotalStatusFileCounts)) {
          const percentages = this.mathService.roundPercentagesToOneHundred(this.checklist.Analytics.TotalStatusFileCounts.map(fileCount => fileCount.Percent));
          return of({ Data: this.getChartData(percentages, this.checklist.Analytics), Statuses: this.getChartStatuses(percentages) });
        } else {
          return of(null);
        }
      })
    )
  }

  private getChartData(percentages: number[], analytics: ProjectChecklistAnalytics): ChartData {
    if (percentages.length > 0) {
      return {
        datasets: [{
          data: percentages,
          backgroundColor: analytics.TotalStatusFileCounts.map(fileCount => this.getThemeColor(fileCount.Legend)),
          borderWidth: analytics.TotalStatusFileCounts.map(() => 0)
        }],
        labels: analytics.TotalStatusFileCounts.map(fileCount => fileCount.Legend)
      };
    } else {
      return {
        datasets: [{
          data: [100],
          backgroundColor: [this.getThemeColor('na')],
          borderWidth: [0]
        }],
        labels: ['']
      };
    }
  }

  private getChartStatuses(percentages: number[]): Statuses {
    return {
      Completed: percentages[0],
      InProgress: percentages[1],
      ToDo: percentages[2]
    };
  }

  private getThemeColor(checklistItemLegend: ProjectChecklistItemLegend | 'na'): string {
    switch (checklistItemLegend) {
      case ProjectChecklistItemLegend.Completed:
        return this.themeService.getColor('checklist-completed');
      case ProjectChecklistItemLegend.InProgress:
        return this.themeService.getColor('checklist-in-progress');
      case ProjectChecklistItemLegend.ToDo:
        return this.themeService.getColor('checklist-todo');
      case 'na':
        return this.themeService.getColor('checklist-na');
      default:
        return this.themeService.getColor('checklist-not-started');
    }
  }
}
